// Menu data based on restaurant-game-design.html

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  image: string;
  ingredients: string[];
  flavors: string[];
  allergens: string[];
  complexity: 'simple' | 'moderate' | 'complex';
  prepTime: number; // in minutes
  mealPeriod: 'breakfast' | 'lunch' | 'snack' | 'dinner';
  steps?: string[]; // optional cooking steps
}

export interface Ingredient {
  id: string;
  name: string;
  image: string;
}

// Available ingredients
export const INGREDIENTS: Ingredient[] = [
  { id: 'flour', name: 'Flour', image: '/images/ingredients/flour.png' },
  { id: 'sugar', name: 'Sugar', image: '/images/ingredients/sugar.png' },
  { id: 'eggs', name: 'Eggs', image: '/images/ingredients/eggs.png' },
  { id: 'milk', name: 'Milk', image: '/images/ingredients/milk.png' },
  { id: 'cheese', name: 'Cheese', image: '/images/ingredients/cheese.png' },
  { id: 'bacon', name: 'Bacon', image: '/images/ingredients/bacon.png' },
  { id: 'salt', name: 'Salt', image: '/images/ingredients/salt.png' },
  { id: 'chocolate', name: 'Chocolate', image: '/images/ingredients/chocolate.png' },
  { id: 'honey', name: 'Honey', image: '/images/ingredients/honey.png' },
  { id: 'strawberry', name: 'Strawberry', image: '/images/ingredients/strawberry.png' },
  { id: 'cherry', name: 'Cherry', image: '/images/ingredients/cherry.png' },
  { id: 'apple', name: 'Apple', image: '/images/ingredients/apple.png' },
  { id: 'lemon', name: 'Lemon', image: '/images/ingredients/lemon.png' },
  { id: 'tomato', name: 'Tomato', image: '/images/ingredients/tomato.png' },
  { id: 'onion', name: 'Onion', image: '/images/ingredients/onion.png' },
  { id: 'bread', name: 'Bread', image: '/images/ingredients/bread.png' },
  { id: 'avocado', name: 'Avocado', image: '/images/ingredients/avocado.png' },
];

// Breakfast Menu
export const BREAKFAST_MENU: MenuItem[] = [
  {
    id: 'pancakes',
    name: 'Fluffy Pancakes',
    description: 'Stack of golden pancakes with syrup',
    image: '/images/food/breakfast/pancakes_dish.png',
    ingredients: ['flour', 'eggs', 'milk', 'sugar'],
    flavors: ['Sweet', 'Buttery'],
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    complexity: 'simple',
    prepTime: 3,
    mealPeriod: 'breakfast',
  },
  {
    id: 'bacon_eggs',
    name: 'Bacon & Eggs',
    description: 'Classic breakfast combo',
    image: '/images/food/breakfast/friedegg.png',
    ingredients: ['eggs', 'bacon', 'salt'],
    flavors: ['Savory', 'Salty'],
    allergens: ['Eggs'],
    complexity: 'simple',
    prepTime: 2,
    mealPeriod: 'breakfast',
  },
  {
    id: 'waffle',
    name: 'Golden Waffle',
    description: 'Crispy waffle with toppings',
    image: '/images/food/breakfast/waffle_dish.png',
    ingredients: ['flour', 'eggs', 'milk', 'strawberry'],
    flavors: ['Sweet', 'Fruity'],
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    complexity: 'moderate',
    prepTime: 4,
    mealPeriod: 'breakfast',
  },
  {
    id: 'bagel',
    name: 'Fresh Bagel',
    description: 'Toasted bagel with cream cheese',
    image: '/images/food/breakfast/bagel.png',
    ingredients: ['bread', 'cheese'],
    flavors: ['Savory', 'Creamy'],
    allergens: ['Gluten', 'Dairy'],
    complexity: 'simple',
    prepTime: 2,
    mealPeriod: 'breakfast',
  },
  {
    id: 'omelet',
    name: 'Cheese Omelet',
    description: 'Fluffy omelet with cheese filling',
    image: '/images/food/breakfast/omelet.png',
    ingredients: ['eggs', 'cheese', 'onion', 'salt'],
    flavors: ['Savory', 'Cheesy'],
    allergens: ['Eggs', 'Dairy'],
    complexity: 'moderate',
    prepTime: 4,
    mealPeriod: 'breakfast',
  },
];

// Lunch Menu
export const LUNCH_MENU: MenuItem[] = [
  {
    id: 'burger',
    name: 'Classic Burger',
    description: 'Juicy beef patty with toppings',
    image: '/images/food/lunch/burger.png',
    ingredients: ['bread', 'cheese', 'tomato', 'onion'],
    flavors: ['Savory', 'Meaty'],
    allergens: ['Gluten', 'Dairy'],
    complexity: 'moderate',
    prepTime: 5,
    mealPeriod: 'lunch',
  },
  {
    id: 'pizza',
    name: 'Pizza Slice',
    description: 'Cheesy pizza with tomato sauce',
    image: '/images/food/lunch/pizza.png',
    ingredients: ['flour', 'tomato', 'cheese'],
    flavors: ['Savory', 'Cheesy'],
    allergens: ['Gluten', 'Dairy'],
    complexity: 'complex',
    prepTime: 6,
    mealPeriod: 'lunch',
  },
  {
    id: 'sandwich',
    name: 'Club Sandwich',
    description: 'Triple-decker with bacon',
    image: '/images/food/lunch/sandwich.png',
    ingredients: ['bread', 'bacon', 'tomato', 'cheese'],
    flavors: ['Savory', 'Smoky'],
    allergens: ['Gluten', 'Dairy'],
    complexity: 'moderate',
    prepTime: 4,
    mealPeriod: 'lunch',
  },
  {
    id: 'hotdog',
    name: 'Classic Hotdog',
    description: 'Grilled dog with mustard',
    image: '/images/food/lunch/hotdog.png',
    ingredients: ['bread', 'onion'],
    flavors: ['Savory', 'Tangy'],
    allergens: ['Gluten'],
    complexity: 'simple',
    prepTime: 3,
    mealPeriod: 'lunch',
  },
  {
    id: 'taco',
    name: 'Taco',
    description: 'Crunchy taco with fresh toppings',
    image: '/images/food/lunch/taco.png',
    ingredients: ['cheese', 'tomato', 'onion'],
    flavors: ['Spicy', 'Savory'],
    allergens: ['Dairy'],
    complexity: 'moderate',
    prepTime: 4,
    mealPeriod: 'lunch',
  },
];

// Snack Menu
export const SNACK_MENU: MenuItem[] = [
  {
    id: 'cookies',
    name: 'Chocolate Chip Cookies',
    description: 'Fresh baked with chocolate chunks',
    image: '/images/food/snack/cookies.png',
    ingredients: ['flour', 'sugar', 'eggs', 'chocolate'],
    flavors: ['Sweet', 'Chocolate'],
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    complexity: 'moderate',
    prepTime: 4,
    mealPeriod: 'snack',
  },
  {
    id: 'donut',
    name: 'Glazed Donut',
    description: 'Classic ring with sweet glaze',
    image: '/images/food/snack/donut.png',
    ingredients: ['flour', 'sugar', 'eggs', 'milk'],
    flavors: ['Sweet', 'Sugary'],
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    complexity: 'complex',
    prepTime: 5,
    mealPeriod: 'snack',
  },
  {
    id: 'apple_pie',
    name: 'Apple Pie Slice',
    description: 'Warm apple filling with crust',
    image: '/images/food/snack/apple_pie.png',
    ingredients: ['flour', 'apple', 'sugar', 'lemon'],
    flavors: ['Sweet', 'Fruity'],
    allergens: ['Gluten', 'Dairy'],
    complexity: 'complex',
    prepTime: 6,
    mealPeriod: 'snack',
  },
  {
    id: 'cheesecake',
    name: 'Cheesecake Slice',
    description: 'Creamy New York style',
    image: '/images/food/snack/cheesecake.png',
    ingredients: ['cheese', 'sugar', 'eggs', 'strawberry'],
    flavors: ['Creamy', 'Sweet'],
    allergens: ['Dairy', 'Eggs', 'Gluten'],
    complexity: 'complex',
    prepTime: 6,
    mealPeriod: 'snack',
  },
  {
    id: 'icecream',
    name: 'Ice Cream Sundae',
    description: 'Scoops with toppings',
    image: '/images/food/snack/icecream.png',
    ingredients: ['milk', 'sugar', 'chocolate', 'cherry'],
    flavors: ['Sweet', 'Cold'],
    allergens: ['Dairy'],
    complexity: 'simple',
    prepTime: 2,
    mealPeriod: 'snack',
  },
];

// Dinner Menu
export const DINNER_MENU: MenuItem[] = [
  {
    id: 'spaghetti',
    name: 'Spaghetti',
    description: 'Pasta with tomato sauce',
    image: '/images/food/dinner/spaghetti.png',
    ingredients: ['flour', 'tomato', 'onion', 'cheese'],
    flavors: ['Savory', 'Tangy'],
    allergens: ['Gluten', 'Dairy'],
    complexity: 'moderate',
    prepTime: 5,
    mealPeriod: 'dinner',
  },
  {
    id: 'ramen',
    name: 'Ramen Bowl',
    description: 'Rich broth with noodles',
    image: '/images/food/dinner/ramen.png',
    ingredients: ['flour', 'eggs', 'onion'],
    flavors: ['Savory', 'Umami'],
    allergens: ['Gluten', 'Eggs'],
    complexity: 'complex',
    prepTime: 6,
    mealPeriod: 'dinner',
  },
  {
    id: 'salmon',
    name: 'Grilled Salmon',
    description: 'Fresh fillet with lemon',
    image: '/images/food/dinner/salmon.png',
    ingredients: ['lemon', 'salt', 'onion'],
    flavors: ['Savory', 'Fresh'],
    allergens: ['Fish'],
    complexity: 'moderate',
    prepTime: 4,
    mealPeriod: 'dinner',
  },
  {
    id: 'curry',
    name: 'Curry Rice',
    description: 'Spiced curry with vegetables',
    image: '/images/food/dinner/curry.png',
    ingredients: ['onion', 'tomato', 'apple'],
    flavors: ['Spicy', 'Savory'],
    allergens: [],
    complexity: 'complex',
    prepTime: 6,
    mealPeriod: 'dinner',
  },
  {
    id: 'steak',
    name: 'Grilled Steak',
    description: 'Juicy steak cooked to order',
    image: '/images/food/dinner/steak.png',
    ingredients: ['salt', 'onion'],
    flavors: ['Savory', 'Meaty'],
    allergens: [],
    complexity: 'moderate',
    prepTime: 5,
    mealPeriod: 'dinner',
  },
];

// Get menu by meal period
export function getMenuByPeriod(period: string): MenuItem[] {
  switch (period.toLowerCase()) {
    case 'breakfast': return BREAKFAST_MENU;
    case 'lunch': return LUNCH_MENU;
    case 'snack': return SNACK_MENU;
    case 'dinner': return DINNER_MENU;
    default: return BREAKFAST_MENU;
  }
}

// Meal period emoji
export function getMealPeriodEmoji(period: string): string {
  switch (period.toLowerCase()) {
    case 'breakfast': return '🌅';
    case 'lunch': return '🌞';
    case 'snack': return '🍪';
    case 'dinner': return '🌙';
    default: return '🍽️';
  }
}

// Complexity color
export function getComplexityColor(complexity: string): string {
  switch (complexity) {
    case 'simple': return '#4caf50';
    case 'moderate': return '#ff9800';
    case 'complex': return '#f44336';
    default: return '#666';
  }
}
